'use client'

import { useRef } from 'react'
import Image from 'next/image'

import type { TextValue } from '@/lib/content-standard/rich-text'
import { cn } from '@/lib/utils'

import { DropdownCaret } from './dropdown-caret'
import { activeLocaleOption, type LocaleOption } from './locale-menu-config'
import { useDismissOnOutsidePress } from './use-dismiss-on-outside-press'

/** 4x3 artwork at navbar scale. */
const FLAG_WIDTH = 21
const FLAG_HEIGHT = 16

interface CountryFlagProps {
    option: LocaleOption
    className?: string
}

/**
 * A country's flag. Always decorative — every flag the picker renders sits beside
 * that country's name (or, on the trigger, beside a screen-reader-only one), so an
 * alt text here would only make assistive tech say the country twice.
 *
 * `unoptimized` is load-bearing, not a micro-optimization: `trailingSlash: true` makes
 * the image optimizer emit broken URLs, and there is no global `images.unoptimized` to
 * fall back on — only this per-call-site prop. Dropping it ships a broken flag.
 */
export function CountryFlag({ option, className }: CountryFlagProps) {
    return (
        <Image
            unoptimized
            src={option.flag}
            alt=""
            width={FLAG_WIDTH}
            height={FLAG_HEIGHT}
            className={cn('rounded-[1px] border border-black/10', className)}
        />
    )
}

interface LocaleMenuProps {
    /** The countries to offer, from `buildLocaleOptions`. */
    options: LocaleOption[]
    /** The picker's purpose, which its accessible name leads with. */
    label: TextValue
}

/** Row geometry, shared so a single-language link and a multi-language row align. */
const ROW_CLASS = 'flex items-center gap-2.5 rounded-md px-3 py-2 text-sm'
const ROW_HOVER =
    'transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground focus:outline-none'

/**
 * One country in the panel.
 *
 * **One language, one link: the whole row.** Flag, name and destination are the same
 * choice, so there is nothing to separate.
 *
 * **Several languages, a link each** (ui-react's `Canada EN FR`). The country name is
 * then plain text rather than a third anchor: legacy makes the row itself resolve to
 * the country's default locale, so `Canada` and `EN` are two click targets for one URL
 * — and legacy's row-level one is a bare clickable `div`, unannounced. Reproducing the
 * shape here would swap that for the other fault: a real, announced anchor going where
 * the first language link already goes. The language links carry the country in their
 * accessible name (`Canada — Français`), so nothing is lost by leaving the name
 * unlinked.
 */
function CountryRow({ option }: { option: LocaleOption }) {
    const [only] = option.languages

    if (option.languages.length === 1) {
        return (
            <a
                href={only.href}
                hrefLang={only.locale}
                aria-current={only.isActive ? 'true' : undefined}
                className={cn(ROW_CLASS, ROW_HOVER, only.isActive && 'font-semibold')}
            >
                <CountryFlag option={option} />
                {option.countryName}
            </a>
        )
    }

    return (
        <div className={cn(ROW_CLASS, 'gap-2')}>
            <CountryFlag option={option} />
            <span className={cn(option.isActive && 'font-semibold')}>{option.countryName}</span>
            {option.languages.map((language) => (
                <a
                    key={language.locale}
                    href={language.href}
                    hrefLang={language.locale}
                    // The visible label is the language subtag (`EN`) — legible in a
                    // narrow chip, meaningless read aloud, so the accessible name
                    // spells out both the country and the language.
                    aria-label={`${option.countryName} — ${language.languageName}`}
                    aria-current={language.isActive ? 'true' : undefined}
                    className={cn(
                        'rounded px-1 text-xs tracking-wide text-muted-foreground',
                        ROW_HOVER,
                        language.isActive && 'font-semibold text-foreground',
                    )}
                >
                    {language.label}
                </a>
            ))}
        </div>
    )
}

/**
 * The header's country picker: the active country's flag, opening a list of the
 * countries nexus serves, each a link to the current page in that country's locale.
 *
 * **A native `<details>`, not the dropdown primitive.** The country links must be
 * anchors in the *served* HTML (the reason the primary menu's dropdowns are
 * CSS-revealed rather than interaction-injected), and the dropdown primitive
 * cannot satisfy that: its content is portalled, so it renders nothing at all
 * server-side and mounts only once the menu is opened. `<details>` keeps the whole
 * list in the markup while giving click-and-keyboard toggling, an announced expanded
 * state, and the chevron's rotation for free — no controlled state, and none of the
 * hover/press phase trap a hover-open listener invites. What the element does not do
 * on its own is close on Escape or an outside press; `useDismissOnOutsidePress`
 * supplies that.
 *
 * Presentational and pathname-blind: the caller resolves the destinations, the same
 * way `AppLayout` builds the menu, logo and account configuration.
 */
export function LocaleMenu({ options, label }: LocaleMenuProps) {
    const active = activeLocaleOption(options)
    const detailsRef = useRef<HTMLDetailsElement>(null)

    useDismissOnOutsidePress(detailsRef)

    // Fewer than two countries is not a choice; an absent active option would leave
    // the trigger blank. Either way the header shows no picker rather than a dead one.
    if (!active || options.length < 2) {
        return null
    }

    return (
        <details ref={detailsRef} className="group/locale relative">
            <summary
                // The purpose AND the current country, in one label on purpose: an
                // `aria-label` *replaces* name-from-content, so a visually-hidden
                // country name inside the control would never be announced (it would
                // still satisfy a `toHaveTextContent` assertion, which is why the
                // binding asserts the accessible name instead).
                aria-label={`${label.text} — ${active.countryName}`}
                className="flex cursor-pointer list-none items-center gap-1 rounded-md px-2 py-2 outline-none hover:bg-accent focus-visible:ring-[3px] focus-visible:ring-ring/50 [&::-webkit-details-marker]:hidden"
            >
                <CountryFlag option={active} />
                <DropdownCaret className="transition duration-300 group-open/locale:rotate-180" />
            </summary>
            {/* `w-max` over a fixed width: three rows is the row count, not the country
                count, so a tenth market becomes a fourth column — and the panel has to
                widen to hold it rather than cram it into a width picked for nine. The
                minimum keeps the three columns from collapsing to their content. */}
            <div className="absolute top-full right-0 z-50 mt-1.5 w-max max-w-[calc(100vw-2rem)] min-w-[34rem] rounded-md border bg-popover p-2 text-popover-foreground shadow">
                {/* Three columns filled top-to-bottom, as ui-react's `MultiColumnLocaleMenu`
                    lays them out: `grid-flow-col` keeps the DOM in the alphabetical order
                    the config emits (what a crawler and a screen reader read) while the
                    eye still runs down each column. Sized in `rows` rather than by
                    chunking the list in JS so the markup stays one flat list.

                    No narrow-viewport variant: the navbar picker itself is `lg:`-only
                    (`navbar.tsx`), and below that breakpoint the sidebar's vertical
                    mirror is the picker. */}
                <ul className="grid list-none auto-cols-fr grid-flow-col grid-rows-3 gap-0.5">
                    {options.map((option) => (
                        <li key={option.countryCode}>
                            <CountryRow option={option} />
                        </li>
                    ))}
                </ul>
            </div>
        </details>
    )
}
